/**
 * GAME — match flow. Turns the sandbox into a match: boots to a menu with a
 * slow-panning camera, starts the clock on DEPLOY, tracks kills/deaths off the
 * canonical damage events, runs the death cam + respawn cycle, and trickles in
 * replacement AI so a 10-minute match doesn't run dry.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * PUBLIC API — `const game = ctx.peek('game')`
 * ────────────────────────────────────────────────────────────────────────────
 *   game.phase        'menu' | 'playing' | 'over'
 *   game.death        null | { killer: string|null, respawnIn: number }
 *   game.stats        { kills, deaths, headshots, streak, bestStreak, timeLeft, matchLength }
 *   game.startMatch()  title-screen DEPLOY button — flips to 'playing', hands
 *                      control back to `player`, requests pointer lock
 *   game.restart()     match-end screen — reloads the page
 *
 * EVENTS EMITTED
 *   game:phase      { phase, prev }         every phase change
 *   game:death      { killer }              after `game.death` is populated
 *   player:respawn  { position, spawnIndex } after the player is moved back in
 *
 * CAPTURE MODE — when `ctx.config.deterministic` or `?capture=1`, this system
 * goes inert: phase is pinned to 'playing' in init and nothing else in this
 * file runs (no menu camera, no timer, no control toggling, no AI trickle), so
 * the pixel-regression harness sees exactly the sandbox it always has.
 */

import * as THREE from 'three';

const RESPAWN_DELAY = 3.5; // seconds from death to control returning
const DEATH_CAM_EASE = 1.1; // seconds for the camera to settle on the ground
const KILLER_ATTRIBUTION_WINDOW = 6; // seconds — last hit within this counts as the killer
const MIN_GARRISON = 6;
const SPAWN_VARIANTS = ['vanguard', 'irregular', 'breacher'];
const PENDING_SPAWN_SLOTS = 16; // generous headroom over MIN_GARRISON deaths at once

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a, b, t) => a + (b - a) * t;

export class GameSystem {
  static id = 'game';
  static deps = ['player', 'world', 'ai', 'weapons'];

  constructor() {
    this.phase = 'menu';
    this.death = null;
    this.stats = {
      kills: 0, deaths: 0, headshots: 0, streak: 0, bestStreak: 0,
      timeLeft: 0, matchLength: 600,
    };
  }

  async init(ctx) {
    this.ctx = ctx;

    const params = new URLSearchParams(location.search);
    this._capture = ctx.config.deterministic || params.get('capture') === '1';
    if (this._capture) {
      // Inert: the harness must see the plain sandbox, not a menu or a clock.
      this.phase = 'playing';
      return;
    }

    this.player = ctx.get('player');
    this.world = ctx.get('world');
    this.ai = ctx.get('ai');
    this.weapons = ctx.get('weapons');
    this.rng = ctx.rng.fork();

    const matchParam = Number(params.get('match'));
    this.stats.matchLength = Number.isFinite(matchParam) && matchParam > 0 ? matchParam : 600;
    this.stats.timeLeft = this.stats.matchLength;

    this.player.setControlEnabled(false);
    this.weapons.setViewmodelVisible(false); // no rifle poking into the menu

    // ---- cinematic menu camera, spawn 0's eye, slow yaw drift -------------
    const sp = this.world.spawn(0);
    this._menuPos = sp?.position ? sp.position.clone() : new THREE.Vector3();
    this._menuYaw = sp?.yaw ?? 0;
    const phys = ctx.peek('physics');
    const gy = phys?.groundHeight(this._menuPos.x, this._menuPos.z, this._menuPos.y + 6);
    this._menuEyeY = (Number.isFinite(gy) ? gy : this._menuPos.y) + 1.62;

    // ---- kill/death tracking ----------------------------------------------
    this._lastAttacker = { name: null, t: -1e9 };
    this._deathEyeStart = { x: 0, y: 0, z: 0 };
    this._deathRollStart = 0;
    this._deathGroundY = 0;
    this._deathEventPayload = { killer: null };
    this._respawnPayload = { position: new THREE.Vector3(), spawnIndex: 0 };
    this._phasePayload = { phase: this.phase, prev: this.phase };

    // ---- match-bar push, preallocated & reused (do not push scoreUs: `ui`
    // owns that counter itself, see src/ui/index.js:201) -------------------
    this._matchPayload = { timeLeft: this.stats.timeLeft, scoreThem: 0 };

    // ---- AI trickle respawn -------------------------------------------------
    this._garrisonSize = Math.max(MIN_GARRISON, this.ai.agents.length);
    this._pendingSpawns = [];
    for (let i = 0; i < PENDING_SPAWN_SLOTS; i++) this._pendingSpawns.push({ t: -1 });

    const on = (type, fn) => (this._off ?? (this._off = [])).push(ctx.events.on(type, fn));
    on('damage:dealt', (e) => this._onDamageDealt(e));
    on('player:death', () => this._onPlayerDeath());
    on('actor:death', () => this._onActorDeath());
  }

  /* ==================================================================== */
  /* phase control                                                        */
  /* ==================================================================== */

  startMatch() {
    if (this._capture || this.phase !== 'menu') return;
    this._setPhase('playing');
    this.player.setControlEnabled(true);
    this.weapons.setViewmodelVisible(true);
    this.ctx.input.requestPointerLock();
  }

  restart() {
    location.reload();
  }

  _setPhase(next) {
    const p = this._phasePayload;
    p.prev = this.phase;
    p.phase = next;
    this.phase = next;
    this.ctx.events.emit('game:phase', p);
  }

  _endMatch() {
    this._setPhase('over');
    this.player.setControlEnabled(false);
    document.exitPointerLock?.();
    this.ctx.time.scale = 0;
  }

  /* ==================================================================== */
  /* frame                                                                */
  /* ==================================================================== */

  update(dt, ctx) {
    if (this._capture || this.phase !== 'playing') return;

    this.stats.timeLeft -= dt;
    const mp = this._matchPayload;
    mp.timeLeft = Math.max(0, this.stats.timeLeft);
    mp.scoreThem = this.stats.deaths;
    ctx.peek('ui')?.setMatch(mp);

    if (this.death) {
      this.death.respawnIn -= dt;
      if (this.death.respawnIn <= 0) this._respawnPlayer();
    }

    this._updatePendingSpawns(dt);

    if (this.stats.timeLeft <= 0) this._endMatch();
  }

  lateUpdate(dt, ctx) {
    if (this._capture) return;
    if (this.phase === 'menu') this._updateMenuCamera(ctx);
    else if (this.death) this._updateDeathCam(dt, ctx);
  }

  /** Control is disabled in 'menu', so the rig has stopped writing the camera
   *  (src/player/index.js:285) — write it ourselves: spawn 0's eye, a slow
   *  yaw drift, dead level otherwise. */
  _updateMenuCamera(ctx) {
    const yaw = this._menuYaw + ctx.time.elapsed * 0.02;
    ctx.camera.position.set(this._menuPos.x, this._menuEyeY, this._menuPos.z);
    ctx.camera.rotation.set(0, yaw, 0);
  }

  /** Control is disabled while dead too, so the rig has released the camera —
   *  ease it down to the ground and roll it, quadratic out over DEATH_CAM_EASE. */
  _updateDeathCam(dt, ctx) {
    const elapsed = Math.min(DEATH_CAM_EASE, RESPAWN_DELAY - Math.max(0, this.death.respawnIn));
    const t = clamp01(elapsed / DEATH_CAM_EASE);
    const eased = 1 - (1 - t) * (1 - t);
    ctx.camera.position.y = lerp(this._deathEyeStart.y, this._deathGroundY, eased);
    ctx.camera.rotation.z = lerp(this._deathRollStart, 0.35, eased);
  }

  /* ==================================================================== */
  /* kill / death tracking                                                */
  /* ==================================================================== */

  /**
   * Runs after `ai`'s own `damage:dealt` listener (this system inits last, see
   * ARCHITECTURE.md dependency order), so `e.killed`/`e.headshot` are already
   * final by the time we read them.
   */
  _onDamageDealt(e) {
    if (!e) return;
    const t = e.target;
    const isPlayer = t === this.player || t === 'player' || t?.isPlayer === true;
    if (isPlayer) {
      this._lastAttacker.name = e.source?.name ?? null;
      this._lastAttacker.t = this.ctx.time.elapsed;
      return;
    }
    if (e.killed === true) {
      this.stats.kills++;
      this.stats.streak++;
      if (this.stats.streak > this.stats.bestStreak) this.stats.bestStreak = this.stats.streak;
      if (e.headshot) this.stats.headshots++;
    }
  }

  _onPlayerDeath() {
    if (this.phase !== 'playing') return;
    this.stats.deaths++;
    this.stats.streak = 0;
    const recent = this.ctx.time.elapsed - this._lastAttacker.t <= KILLER_ATTRIBUTION_WINDOW;
    const killer = recent ? this._lastAttacker.name : null;
    this.death = { killer, respawnIn: RESPAWN_DELAY };

    const ctx = this.ctx;
    const cam = ctx.camera;
    this._deathEyeStart.x = cam.position.x;
    this._deathEyeStart.y = cam.position.y;
    this._deathEyeStart.z = cam.position.z;
    this._deathRollStart = cam.rotation.z;
    const phys = ctx.peek('physics');
    const gy = phys?.groundHeight(cam.position.x, cam.position.z, cam.position.y + 6);
    this._deathGroundY = (Number.isFinite(gy) ? gy : cam.position.y - 1.6) + 0.35;

    this.player.setControlEnabled(false);
    this.weapons.setViewmodelVisible(false); // the death cam is a body, not a shooter
    this._deathEventPayload.killer = killer;
    ctx.events.emit('game:death', this._deathEventPayload);
  }

  _respawnPlayer() {
    const ctx = this.ctx;
    const index = this._pickSpawnIndex();
    this.player.respawn(index);
    this.weapons.refillAmmo();
    this.weapons.setViewmodelVisible(true);
    this.player.setControlEnabled(true);
    this.death = null;

    const sp = this.world.spawn(index);
    const rp = this._respawnPayload;
    if (sp?.position) rp.position.copy(sp.position);
    rp.spawnIndex = index;
    ctx.events.emit('player:respawn', rp);
  }

  /** Maximise distance to the nearest living AI agent; farthest from the
   *  death position breaks ties (including the "no agents alive" case, where
   *  every spawn ties at Infinity). */
  _pickSpawnIndex() {
    const spawns = this.world.spawnPoints;
    const agents = this.ai.agents;
    const deathPos = this._deathEyeStart;
    let bestIdx = 0, bestMinD = -1, bestDeathD = -1;
    for (let i = 0; i < spawns.length; i++) {
      const pos = spawns[i].position;
      let minD = Infinity;
      for (let j = 0; j < agents.length; j++) {
        const a = agents[j];
        if (!a.alive) continue;
        const d = pos.distanceTo(a.position);
        if (d < minD) minD = d;
      }
      const dx = pos.x - deathPos.x, dz = pos.z - deathPos.z;
      const deathD = Math.hypot(dx, dz);
      if (minD > bestMinD || (minD === bestMinD && deathD > bestDeathD)) {
        bestMinD = minD;
        bestDeathD = deathD;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  /* ==================================================================== */
  /* AI trickle respawn                                                   */
  /* ==================================================================== */

  _onActorDeath() {
    if (this.phase !== 'playing') return;
    for (const slot of this._pendingSpawns) {
      if (slot.t < 0) {
        slot.t = this.rng.range(6, 12);
        return;
      }
    }
    // every slot busy — an unlikely wipe-and-respawn pileup; the next death
    // will find a freed slot.
  }

  _updatePendingSpawns(dt) {
    for (const slot of this._pendingSpawns) {
      if (slot.t < 0) continue;
      slot.t -= dt;
      if (slot.t > 0) continue;
      slot.t = -1;
      this._trySpawnReplacement();
    }
  }

  _trySpawnReplacement() {
    let alive = 0;
    const agents = this.ai.agents;
    for (let i = 0; i < agents.length; i++) if (agents[i].alive) alive++;
    if (alive >= this._garrisonSize) return;

    const spawns = this.world.spawnPoints;
    if (!spawns.length) return;
    const p = this.player.position;
    let bestIdx = 0, bestD = -1;
    for (let i = 0; i < spawns.length; i++) {
      const d = spawns[i].position.distanceTo(p);
      if (d > bestD) { bestD = d; bestIdx = i; }
    }
    const sp = spawns[bestIdx];
    const variant = SPAWN_VARIANTS[this.rng.int(0, SPAWN_VARIANTS.length - 1)];
    this.ai.spawn(variant, sp.position, sp.yaw);
  }

  dispose() {
    for (const off of this._off ?? []) off();
  }
}
